import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { ListView } from '@app/shared/utils/list-view';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { liveReload } from '@shared/services/realtime.service';

import { LogisticsCatalogs, TransferRequestsService, fmtDate, statusChip, errMsg } from '../logistics.service';
import { canSupplyRequests } from '../logistics.service';
import { TransferRequestFormDialogComponent } from './transfer-request-form-dialog.component';
import { TransferFormDialogComponent } from '../transfers/transfer-form-dialog.component';

export const TRANSFER_REQUEST_STATUSES = ['Nueva', 'En proceso', 'Atendida', 'Cancelada'];

/**
 * Solicitud de traspaso (SOT-n): material entre el locker y el in-house de un
 * mismo hospital. Se registra aquí y se atiende con un traspaso (botón
 * "Atender" o desde Traspasos eligiendo la solicitud).
 */
@Component({
  selector: 'app-transfer-requests',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, MatTableModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule, MatButtonToggleModule, MatDatepickerModule, MatDialogModule,
    KpiStripComponent, PageLoaderComponent, PageTitleComponent, SearchPillComponent, FilterChipsComponent],
  styleUrl: '../logistics.scss',
  template: `
<app-page-title [title]="title()"></app-page-title>
<app-page-loader [load]="pageLoad" [label]="title()" (retry)="init()"></app-page-loader>

<app-kpi-strip [kpis]="kpis" [active]="activeKpi" (activeChange)="setKpi($event)"></app-kpi-strip>

<div class="table-section">
  <div class="filter-bar filter-bar--flush">
    <div class="filter-card">
      <app-search-pill [value]="searchText" placeholder="Folio, hospital, código, quien solicita…" (valueChange)="applySearch($event)"></app-search-pill>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Periodo</mat-label>
        <mat-date-range-input [rangePicker]="periodPicker">
          <input matStartDate [(ngModel)]="periodStart" placeholder="Inicio" (dateChange)="load()">
          <input matEndDate [(ngModel)]="periodEnd" placeholder="Fin" (dateChange)="load()">
        </mat-date-range-input>
        <mat-datepicker-toggle matSuffix [for]="periodPicker"></mat-datepicker-toggle>
        <mat-date-range-picker #periodPicker></mat-date-range-picker>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Hospital</mat-label>
        <mat-select [(value)]="placeFilter" (selectionChange)="load()">
          <mat-option [value]="''">Todos</mat-option>
          @for (p of placeOptions; track p.id) { <mat-option [value]="p.id">{{ p.alias || p.name }}</mat-option> }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Estado</mat-label>
        <mat-select [(value)]="statusFilter" (selectionChange)="load()">
          <mat-option [value]="''">Todos</mat-option>
          @for (s of statuses; track s) { <mat-option [value]="s">{{ s }}</mat-option> }
        </mat-select>
      </mat-form-field>
      <mat-button-toggle-group class="view-toggle" [value]="listView.mode" (change)="listView.set($event.value)" [hideSingleSelectionIndicator]="true" aria-label="Vista">
        <mat-button-toggle value="table" matTooltip="Vista de tabla"><mat-icon>table_rows</mat-icon></mat-button-toggle>
        <mat-button-toggle value="cards" matTooltip="Vista de tarjetas"><mat-icon>grid_view</mat-icon></mat-button-toggle>
      </mat-button-toggle-group>
    </div>
    <app-filter-chips [chips]="filterChips" [count]="dataSource.filteredData.length" [total]="dataSource.data.length" noun="solicitudes" (remove)="removeChip($event)" (clear)="clearFilters()"></app-filter-chips>
  </div>

  <div class="list-actions">
    <button mat-stroked-button (click)="pageLoad.refresh(load())"><mat-icon>refresh</mat-icon> Actualizar</button>
    <button mat-stroked-button (click)="downloadExcel()" [disabled]="!dataSource.filteredData.length"><mat-icon>download</mat-icon> Descargar Excel</button>
    <button mat-flat-button color="primary" (click)="openForm()"><mat-icon>add</mat-icon> Nueva solicitud de traspaso</button>
  </div>

  <mat-paginator [hidden]="listView.mode !== 'table'" [pageSize]="10" [pageSizeOptions]="[5, 10, 25]" showFirstLastButtons></mat-paginator>

  <ng-template #rowMenu let-r>
    @if (r.status === 'Nueva' || r.status === 'En proceso') {
      <button mat-menu-item (click)="attend(r)"><mat-icon>swap_horiz</mat-icon> Atender (hacer traspaso)</button>
      <button mat-menu-item (click)="cancel(r)"><mat-icon>cancel</mat-icon> Cancelar</button>
    } @else {
      <button mat-menu-item disabled><mat-icon>lock</mat-icon> Sin acciones</button>
    }
  </ng-template>

  @if (listView.mode === 'cards') {
    <div class="inv-cards cat-cards">
      @for (r of dataSource.filteredData; track r.id) {
        <div class="cat-card">
          <div class="cat-card__head">
            <span class="cat-card__title">{{ r.folio }}</span>
            <span [class]="statusChip(r.status)">{{ r.status }}</span>
          </div>
          <div class="cat-card__body">
            <div class="cat-card__field"><span class="cat-card__label">Solicita:</span><span class="cat-card__value">{{ r.requestedBy || '—' }}</span></div>
            <div class="cat-card__field"><span class="cat-card__label">Hospital:</span><span class="cat-card__value">{{ r._place }}</span></div>
            <div class="cat-card__field"><span class="cat-card__label">Traspaso:</span><span class="cat-card__value">{{ r._from }} → {{ r._to }} · {{ r._direction }}</span></div>
            <div class="cat-card__field"><span class="cat-card__label">Material:</span><span class="cat-card__value">{{ r._codes }}</span></div>
            <div class="cat-card__field"><span class="cat-card__label">Surtido:</span><span class="cat-card__value">{{ r._supplied }} / {{ r._qty }}</span></div>
          </div>
          <div class="cat-card__actions">
            <button mat-icon-button [matMenuTriggerFor]="cm" matTooltip="Acciones"><mat-icon>more_vert</mat-icon></button>
            <mat-menu #cm="matMenu"><ng-container *ngTemplateOutlet="rowMenu; context: { $implicit: r }"></ng-container></mat-menu>
          </div>
        </div>
      }
      @if (dataSource.filteredData.length === 0) { <p class="inv-cards__empty">Sin solicitudes con estos filtros</p> }
    </div>
  }

  <div class="table-container mat-elevation-z2" [hidden]="listView.mode !== 'table'">
    <table mat-table [dataSource]="dataSource">
      <ng-container matColumnDef="folio">
        <th mat-header-cell *matHeaderCellDef>Folio</th>
        <td mat-cell *matCellDef="let r"><code class="folio-badge">{{ r.folio }}</code></td>
      </ng-container>
      <ng-container matColumnDef="createdOn">
        <th mat-header-cell *matHeaderCellDef>Fecha</th>
        <td mat-cell *matCellDef="let r" class="nowrap">{{ fmtDate(r.createdOn) }}</td>
      </ng-container>
      <ng-container matColumnDef="requestedBy">
        <th mat-header-cell *matHeaderCellDef>Solicita</th>
        <td mat-cell *matCellDef="let r">{{ r.requestedBy || '—' }}</td>
      </ng-container>
      <ng-container matColumnDef="place">
        <th mat-header-cell *matHeaderCellDef>Hospital</th>
        <td mat-cell *matCellDef="let r">{{ r._place }}</td>
      </ng-container>
      <ng-container matColumnDef="route">
        <th mat-header-cell *matHeaderCellDef>Sale de → Entra a</th>
        <td mat-cell *matCellDef="let r">{{ r._from }} → {{ r._to }}<span class="sub">{{ r._direction }}</span></td>
      </ng-container>
      <ng-container matColumnDef="lines">
        <th mat-header-cell *matHeaderCellDef>Material</th>
        <td mat-cell *matCellDef="let r">{{ r._codes }}</td>
      </ng-container>
      <ng-container matColumnDef="progress">
        <th mat-header-cell *matHeaderCellDef>Surtido</th>
        <td mat-cell *matCellDef="let r">
          <div class="progress"><div class="progress__bar"><div class="progress__fill" [style.width.%]="r._pct"></div></div><span>{{ r._supplied }}/{{ r._qty }}</span></div>
        </td>
      </ng-container>
      <ng-container matColumnDef="neededBy">
        <th mat-header-cell *matHeaderCellDef>Se necesita</th>
        <td mat-cell *matCellDef="let r" class="nowrap" [class.flag]="r._late">{{ fmtDate(r.neededBy, false) || '—' }}</td>
      </ng-container>
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Estado</th>
        <td mat-cell *matCellDef="let r"><span [class]="statusChip(r.status)">{{ r.status }}</span></td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let r" class="nowrap">
          @if (r.status === 'Nueva' || r.status === 'En proceso') {
            <button mat-icon-button color="primary" matTooltip="Atender (hacer traspaso)" (click)="attend(r)"><mat-icon>swap_horiz</mat-icon></button>
          }
          <button mat-icon-button [matMenuTriggerFor]="tm" matTooltip="Más acciones"><mat-icon>more_vert</mat-icon></button>
          <mat-menu #tm="matMenu"><ng-container *ngTemplateOutlet="rowMenu; context: { $implicit: r }"></ng-container></mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: columns;" class="table-row"></tr>
      <tr class="no-data-row" *matNoDataRow>
        <td [attr.colspan]="columns.length"><div class="no-data"><mat-icon>swap_horiz</mat-icon><span>Sin solicitudes con estos filtros</span></div></td>
      </tr>
    </table>
  </div>
</div>
  `,
})
export class TransferRequestsComponent implements OnInit {
  private readonly modules = inject(ModulesService);
  readonly title = this.modules.moduleTitle('/transferRequests', 'Solicitud de traspaso');

  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransferRequestsService);
  private readonly dialog = inject(MatDialog);
  private readonly msg = inject(CustomMessageService);
  private readonly excel = inject(SimpleExcelService);
  private readonly scopeService = inject(UserScopeService);
  private scope: UserScope = { places: null, specialties: null };

  readonly statuses = TRANSFER_REQUEST_STATUSES;
  readonly fmtDate = fmtDate;
  readonly statusChip = statusChip;
  readonly listView = new ListView('transferRequests', 'table');
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['transferRequests', 'transfers'], () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));

  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  statusFilter = '';
  placeFilter = '';
  searchText = '';

  columns = ['folio', 'createdOn', 'requestedBy', 'place', 'route', 'lines', 'progress', 'neededBy', 'status', 'actions'];
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI: Record<string, (r: any) => boolean> = {
    new: r => r.status === 'Nueva',
    progress: r => r.status === 'En proceso',
    done: r => r.status === 'Atendida',
    late: r => r._late,
  };

  get placeOptions(): any[] { return this.cat.places.filter(p => inScope(this.scope.places, p.id, false) && this.cat.sourceKind({ place: p.id }) === 'HOSPITAL'); }

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.initialLoad()); }

  private async initialLoad(): Promise<void> {
    try { this.scope = await this.scopeService.load(); } catch { /* sin alcance */ }
    await this.cat.load();
    await this.load();
  }

  async load(): Promise<void> {
    try {
      const condition: any = { deleted: false };
      // El hospital se filtra abajo (origen o destino).
      const r: any = await this.service.search({ searchType: 'default', condition });
      const today = localDay(new Date());
      const wName = (id: any) => this.cat.warehousesById[id]?.name ?? '—';
      this.allRows = ((r?.data ?? []) as any[])
        .filter(x => inScope(this.scope.places, x.place) || inScope(this.scope.places, x.toPlace))
        .filter(x => !this.placeFilter || x.place === this.placeFilter || x.toPlace === this.placeFilter)
        .filter(x => !this.statusFilter || x.status === this.statusFilter)
        .filter(x => inDateRange(x.createdOn, this.periodStart, this.periodEnd))
        .map(x => {
          const lines: any[] = x.lines ?? [];
          const qty = lines.reduce((a, l) => a + (Number(l.quantity) || 0), 0);
          const sup = lines.reduce((a, l) => a + Math.min(Number(l.supplied) || 0, Number(l.quantity) || 0), 0);
          return {
            ...x, _place: x.toPlace && x.toPlace !== x.place ? `${this.cat.placeName(x.place)} → ${this.cat.placeName(x.toPlace)}` : this.cat.placeName(x.place),
            _direction: ({ IN_TO_LOCKER: 'In-house → Locker', LOCKER_TO_IN: 'Locker → In-house', HOSPITAL_TO_HOSPITAL: 'Hospital → Hospital', LOCKER_TO_LOCKER: 'Locker → Locker', LOCKER_TO_HOSPITAL: 'Hospital → Hospital' } as any)[x.direction] ?? (this.cat.isInhouse(this.cat.warehousesById[x.fromWarehouse]) ? 'In-house → Locker' : 'Locker → In-house'), _from: x.fromWarehouse ? wName(x.fromWarehouse) : this.cat.placeName(x.place),
            _to: x.toWarehouse ? wName(x.toWarehouse) : this.cat.placeName(x.toPlace),
            _qty: qty, _supplied: sup, _pct: qty ? Math.round(100 * sup / qty) : 0,
            _codes: lines.map(l => `${l.code} ×${l.quantity}`).join(', '),
            _late: !!x.neededBy && String(x.neededBy).slice(0, 10) < today && ['Nueva', 'En proceso'].includes(x.status),
          };
        })
        .sort((a, b) => String(b.folio ?? '').localeCompare(String(a.folio ?? ''), undefined, { numeric: true }));
      this.dataSource.filterPredicate = (row, f) => [row.folio, row._place, row._from, row._to, row.requestedBy, row._codes, row.status].join(' ').toLowerCase().includes(f);
      this.refreshKpis();
      this.dataSource.paginator = this.paginator;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar las solicitudes.'), dismissBtn: true });
    }
  }

  private refreshKpis(): void {
    const c = (k: string) => this.allRows.filter(TransferRequestsComponent.KPI[k]).length;
    this.kpis = [
      { key: 'total', label: 'Solicitudes', icon: 'swap_horiz', value: this.allRows.length, isTotal: true },
      { key: 'new', label: 'Nuevas', icon: 'fiber_new', value: c('new'), tone: 'warn' },
      { key: 'progress', label: 'En proceso', icon: 'pending_actions', value: c('progress') },
      { key: 'done', label: 'Atendidas', icon: 'task_alt', value: c('done'), tone: 'ok' },
      { key: 'late', label: 'Vencidas', icon: 'alarm', value: c('late'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(k: string | null): void { this.activeKpi = k; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? TransferRequestsComponent.KPI[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  applySearch(v: string): void { this.searchText = v ?? ''; this.dataSource.filter = this.searchText.trim().toLowerCase(); }

  get filterChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.placeFilter) c.push({ key: 'place', label: 'Hospital', value: this.cat.placeName(this.placeFilter) });
    if (this.statusFilter) c.push({ key: 'status', label: 'Estado', value: this.statusFilter });
    if (this.periodStart || this.periodEnd) c.push({ key: 'period', label: 'Periodo', value: `${fmtDate(this.periodStart, false) || '…'} – ${fmtDate(this.periodEnd, false) || '…'}`, removable: false });
    return c;
  }
  async removeChip(k: string): Promise<void> {
    if (k === 'place') this.placeFilter = '';
    if (k === 'status') this.statusFilter = '';
    await this.load();
  }
  async clearFilters(): Promise<void> {
    this.placeFilter = ''; this.statusFilter = '';
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    this.applySearch('');
    await this.load();
  }

  openForm(): void {
    this.dialog.open(TransferRequestFormDialogComponent, {
      data: { scopePlaces: this.scope.places, canCrossHospital: canSupplyRequests(this.scope.roles) }, width: '760px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: 'first-tabbable',
    }).afterClosed().subscribe(ok => { if (ok) void this.load(); });
  }

  /** Abre el traspaso con la solicitud ya elegida (origen, destino y lo pedido). */
  attend(r: any): void {
    this.dialog.open(TransferFormDialogComponent, {
      data: { prefill: { transferRequest: r.id, transferRequestRecord: r } }, width: '960px', maxWidth: '96vw', maxHeight: '92vh',
    }).afterClosed().subscribe(ok => { if (ok) void this.load(); });
  }

  async cancel(r: any): Promise<void> {
    const yes = await this.msg.open({ title: 'Cancelar solicitud', type: 'warning', acceptBtn: true, dismissBtn: true,
      acceptBtnText: 'Sí, cancelar', dismissBtnText: 'No', message: `¿Cancelar la solicitud de traspaso <b>${r.folio}</b>?` });
    if (!yes) return;
    try {
      await this.service.update({ id: r.id, status: 'Cancelada', operationType: 'single' });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo cancelar.'), dismissBtn: true });
    }
  }

  async downloadExcel(): Promise<void> {
    const rows = (this.dataSource.filteredData ?? []) as any[];
    const data: any[] = [];
    for (const r of rows) for (const l of (r.lines ?? [])) data.push({
      folio: r.folio, date: fmtDate(r.createdOn), requestedBy: r.requestedBy ?? '', place: r._place, from: r._from, to: r._to,
      neededBy: fmtDate(r.neededBy, false), code: l.code, description: l.description, quantity: l.quantity,
      supplied: l.supplied ?? 0, status: r.status,
    });
    const cols = [['folio', 'Folio'], ['date', 'Fecha'], ['requestedBy', 'Solicita'], ['place', 'Hospital'], ['from', 'Sale de'],
      ['to', 'Entra a'], ['neededBy', 'Se necesita'], ['code', 'Código'], ['description', 'Descripción'], ['quantity', 'Piezas'],
      ['supplied', 'Surtidas'], ['status', 'Estado']].map(([k, n]) => ({ columnKey: k, columnName: n, type: 'string' as const }));
    await this.excel.simpleXLSX({ fileName: `Solicitudes de traspaso ${localDay(new Date())}`,
      sheets: [{ sheetName: 'Solicitudes', columns: cols as any, data, header: [], lineBreak: 0 }] });
  }
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
